import type { ExperienceTestTypeDto } from "@intelligems/ig-types/dtos/experiences";
import type { WebUiScopesConfigurationKeyType } from "@intelligems/ig-types/dtos/configurations";
import type { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";

export type ScopeAction = "create" | "start";

export type ScopeKey = Lowercase<WebUiScopesConfigurationKeyType>;

export type RequiredScope = {
  key: ScopeKey;
  /** The test type the scope is for, as the merchant knows it. */
  label: string;
};

type TestType = keyof ExperienceTestTypeDto;

const SCOPES: Record<ScopeAction, Record<TestType, ScopeKey>> = {
  create: {
    hasTestPricing: "create_price_experiment",
    hasTestShipping: "create_shipping_experiment",
    hasTestContentTemplate: "create_template_experiment",
    hasTestContentTheme: "create_theme_experiment",
    hasTestContent: "create_onsite_edits_experiment",
    hasTestCampaign: "create_discount_experiment",
    hasTestContentAdvanced: "create_content_experiment", // deprecated?
    hasTestContentOnsite: "create_onsite_edits_experiment",
    hasTestContentUrl: "create_redirect_experiment",
    hasTestOnsiteInjections: "create_onsite_edits_experiment",
    hasTestCheckoutBlocks: "create_checkout_block_test",
    hasTestPostPurchase: "create_checkout_block_test",
    hasTestOnsiteUpsell: "create_onsite_upsell",
  },
  start: {
    hasTestPricing: "start_price_experiment",
    hasTestShipping: "start_shipping_experiment",
    hasTestContentTemplate: "start_template_experiment",
    hasTestContentTheme: "start_theme_experiment",
    hasTestContent: "start_onsite_edits_experiment",
    hasTestCampaign: "start_discount_experiment",
    hasTestContentAdvanced: "start_content_experiment", // deprecated?
    hasTestContentOnsite: "start_onsite_edits_experiment",
    hasTestContentUrl: "start_redirect_experiment",
    hasTestOnsiteInjections: "start_onsite_edits_experiment",
    hasTestCheckoutBlocks: "start_checkout_block_test",
    hasTestPostPurchase: "start_checkout_block_test",
    hasTestOnsiteUpsell: "start_onsite_upsell",
  },
};

const PERSONALIZATION_OFFER_SCOPES: Record<ScopeAction, ScopeKey> = {
  create: "create_offer",
  start: "start_offer",
};

const LABELS: Record<TestType, string> = {
  hasTestPricing: "pricing",
  hasTestShipping: "shipping",
  hasTestContentTemplate: "template",
  hasTestContentTheme: "theme",
  hasTestContent: "onsite edits",
  hasTestCampaign: "discount",
  hasTestContentAdvanced: "content", // deprecated?
  hasTestContentOnsite: "onsite edits",
  hasTestContentUrl: "redirect",
  hasTestOnsiteInjections: "onsite edits",
  hasTestCheckoutBlocks: "checkout",
  hasTestPostPurchase: "post-purchase",
  hasTestOnsiteUpsell: "onsite upsell",
};

type ScopeOptions = {
  action: ScopeAction;
  testTypes: Partial<ExperienceTestTypeDto>;
  category: ExperienceCategory;
};

/** The WEB_UI scopes an experience needs for the action, one per enabled test type. */
export const getRequiredScopes = ({
  action,
  testTypes,
  category,
}: ScopeOptions): RequiredScope[] =>
  (Object.keys(testTypes) as TestType[])
    .filter((testType) => testTypes[testType])
    .map((testType) => ({
      key:
        testType === "hasTestCampaign" && category === "personalization"
          ? PERSONALIZATION_OFFER_SCOPES[action]
          : SCOPES[action][testType],
      label: LABELS[testType],
    }));

/** The required scopes that are not among the granted `scopes`. */
export const getMissingScopes = ({
  scopes,
  ...options
}: ScopeOptions & { scopes: readonly ScopeKey[] }): RequiredScope[] =>
  getRequiredScopes(options).filter(({ key }) => !scopes.includes(key));
